import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import InfoOnlyBadge from '../ui/InfoOnlyBadge';
import { isRealEmail } from '../../lib/email';
import { useAuth } from '../../lib/auth';
import { formatPhoneInput, isCompletePhone, displayPhone } from '../../lib/phone';

// Owners section on the horse detail page.
// Read-only for everyone; add/remove gated to owner + manager.
// `owners` comes from GET /horses/:id (so adding/removing just refetches it).
export default function OwnersCard({ horseId, owners = [] }) {
  const { activeRole } = useAuth();
  const canEdit = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const remove = useMutation({
    mutationFn: (userId) => api.del(`/horses/${horseId}/owners/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['horse', horseId] }),
  });

  return (
    <section className="card p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium">Owners &amp; lessees</h2>
        {canEdit && !adding && (
          <button onClick={() => setAdding(true)} className="text-sm text-ink-600 hover:text-ink-900">
            + Add Owner
          </button>
        )}
      </div>

      {owners.length === 0 && !adding && (
        <p className="text-sm text-ink-400">No owners or lessees on file.</p>
      )}

      {owners.length > 0 && (
        <ul className="divide-y divide-ink-100">
          {owners.map((o) => (
            <li key={o.userId} className="py-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink-900 truncate">{o.name || o.email}</span>
                  {o.role === 'lessee' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      Lessee
                    </span>
                  )}
                  {o.billingContact && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      Billing
                    </span>
                  )}
                  {/* A share only means something for an owner. */}
                  {o.role !== 'lessee' && o.ownershipPct != null && o.ownershipPct !== 100 && (
                    <span className="text-xs text-ink-500">{o.ownershipPct}%</span>
                  )}
                </div>
                <div className="text-sm text-ink-500 flex flex-wrap items-center gap-2">
                  <span className="truncate">
                    {isRealEmail(o.email)
                      ? o.email
                      : <span className="text-ink-300">No email</span>}
                    {o.phone ? ` · ${displayPhone(o.phone)}` : ''}
                  </span>
                  {!isRealEmail(o.email) && <InfoOnlyBadge />}
                </div>
              </div>
              {canEdit && (
                <button
                  onClick={() => {
                    const what = o.role === 'lessee' ? 'a lessee' : 'an owner';
                    if (confirm(`Remove ${o.name || o.email} as ${what}?`)) remove.mutate(o.userId);
                  }}
                  disabled={remove.isPending}
                  className="text-ink-300 hover:text-red-600 shrink-0"
                  aria-label="Remove owner"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && <AddOwnerForm horseId={horseId} onDone={() => setAdding(false)} />}
    </section>
  );
}

function AddOwnerForm({ horseId, onDone }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [pct, setPct] = useState('');
  const [billing, setBilling] = useState(false);
  const [role, setRole] = useState('owner'); // 'owner' | 'lessee'
  const isLessee = role === 'lessee';

  const add = useMutation({
    mutationFn: () =>
      api.post(`/horses/${horseId}/owners`, {
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        // The server forces 0% for a lessee, so don't bother sending a share.
        ownershipPct: isLessee || pct === '' ? undefined : Number(pct),
        billingContact: billing,
        role,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['horse', horseId] });
      onDone();
    },
  });

  const phoneOk = isCompletePhone(phone);
  const canSubmit = name.trim() && phoneOk && !add.isPending;

  return (
    <div className="mt-3 pt-3 border-t border-ink-100 space-y-2">
      <div className="flex gap-1">
        {[
          ['owner', 'Owner'],
          ['lessee', 'Lessee'],
        ].map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setRole(v)}
            className={
              'text-sm px-3 py-1.5 rounded-full ' +
              (role === v ? 'bg-ink-800 text-white' : 'bg-ink-100 text-ink-600')
            }
          >
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-ink-400">
        {isLessee
          ? 'Rides this horse under lease. Doesn’t own a share.'
          : 'Owns this horse, in whole or in part.'}
      </p>

      <input
        className="input"
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="input"
        type="email"
        placeholder="Email (optional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div>
        <input
          className="input"
          type="tel"
          inputMode="numeric"
          placeholder="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
        />
        {!phoneOk && (
          <p className="mt-1 text-xs text-amber-600">Enter a 10-digit phone number.</p>
        )}
      </div>
      <div className="flex items-center gap-3">
        {!isLessee && (
          <input
            className="input w-28"
            type="number"
            min="0"
            max="100"
            placeholder="Owner %"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
          />
        )}
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <input type="checkbox" checked={billing} onChange={(e) => setBilling(e.target.checked)} />
          Billing contact
        </label>
      </div>

      {add.isError && <p className="text-sm text-red-600">{friendlyError(add.error)}</p>}

      <div className="flex gap-2 pt-1">
        <button onClick={() => add.mutate()} disabled={!canSubmit} className="btn-primary">
          {add.isPending ? 'Adding…' : isLessee ? 'Add Lessee' : 'Add Owner'}
        </button>
        <button onClick={onDone} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </div>
  );
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'missing_fields':
      return 'Name and email are both required.';
    case 'invalid_email':
      return 'That email does not look right.';
    case 'already_owner':
      return 'That person is already an owner of this horse.';
    case 'invalid_pct':
      return 'Ownership % must be between 0 and 100.';
    case 'forbidden':
      return 'Only an owner or manager can add owners.';
    default:
      return 'Could not add owner. Try again.';
  }
}
